import { Calendar, CheckCircle, Upload, X } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { HipaaAuthorizationSchema } from '@/services/api/hipaaAuthorizationService';

interface SignatureUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, signatureDate: string) => void;
  onClear?: () => void;
  isUploading?: boolean;
  existingAuthorization?: HipaaAuthorizationSchema | null;
  zIndex?: number;
}

export const SignatureUploadModal: React.FC<SignatureUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  onClear,
  isUploading = false,
  existingAuthorization,
  zIndex,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [signatureDate, setSignatureDate] = useState<string>(
    existingAuthorization?.signature_date?.split('T')[0] ||
      new Date().toISOString().split('T')[0],
  );
  const [preview, setPreview] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else if (file) {
      setSelectedFile(file);
      setPreview(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFile && signatureDate) {
      onUpload(selectedFile, signatureDate);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto z-[100]"
        zIndex={zIndex}
      >
        <DialogHeader>
          <DialogTitle>Upload HIPAA Authorization Signature</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Guidelines Section */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Clean Signature Guidelines
              </h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">
                    Signature only - no background text or lines
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">Clear and in focus</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-xs">Fills most of the image</span>
                </div>
              </div>
            </div>

            {/* Example Images */}
            <div>
              <h4 className="text-xs font-medium text-gray-900 mb-3">
                Examples
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center">
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-3 mb-2">
                    <div className="bg-white rounded p-2">
                      <div className="text-lg font-signature text-gray-800 italic">
                        John A. Smith
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-green-700 font-medium">
                    ✓ Good
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Clean signature only
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 mb-2">
                    <div className="bg-gray-200 rounded p-2">
                      <div className="text-lg font-signature text-gray-400 italic blur-sm">
                        John A. Smith
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-red-700 font-medium">
                    ✗ Poor
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Blurry with background clutter
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Tip:</strong> Screenshot or crop the signature area to
                remove any background text or lines.
              </p>
            </div>
          </div>

          {/* Upload Section */}
          <div className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!selectedFile ? (
                <div
                  {...getRootProps()}
                  className={cn(
                    'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors min-h-[200px] flex flex-col justify-center',
                    isDragActive
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-300 hover:border-gray-400',
                  )}
                >
                  <input {...getInputProps()} />
                  <Upload className="mx-auto h-8 w-8 text-gray-400 mb-3" />
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    {isDragActive
                      ? 'Drop the signature file here'
                      : 'Upload signature file'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Drag & drop or click to browse
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Supports PNG, JPG, JPEG, WebP, PDF
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      {preview && (
                        <div className="w-20 h-16 border rounded overflow-hidden bg-white">
                          <img
                            src={preview}
                            alt="Signature preview"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium truncate max-w-xs">
                          {selectedFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreview(null);
                      }}
                      className="h-7 w-7 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {preview && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-2">Preview:</p>
                      <div className="bg-white border rounded p-3 max-h-32 overflow-hidden">
                        <img
                          src={preview}
                          alt="Full signature preview"
                          className="max-w-full max-h-24 object-contain mx-auto"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Signature Date Input */}
              <div className="space-y-2">
                <Label
                  htmlFor="signature-date"
                  className="flex items-center gap-2 text-sm"
                >
                  <Calendar className="h-4 w-4" />
                  Signature Date
                </Label>
                <Input
                  id="signature-date"
                  type="date"
                  value={signatureDate}
                  onChange={(e) => setSignatureDate(e.target.value)}
                  required
                  max={new Date().toISOString().split('T')[0]}
                  className="text-sm"
                />
                <p className="text-xs text-gray-500">
                  When was this HIPAA authorization signed?
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-between pt-4 border-t">
                <div>
                  {onClear && existingAuthorization && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        onClear();
                        handleClose();
                      }}
                      disabled={isUploading}
                      className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                    >
                      Clear Signature
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isUploading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!selectedFile || !signatureDate || isUploading}
                    className="gap-2"
                  >
                    {isUploading ? (
                      <>
                        <div className="h-3 w-3 animate-spin rounded-full border border-white border-t-transparent" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-3 w-3" />
                        Upload Signature
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
